export enum ELicenceLevelType {
  Start = 'start',
  Plus = 'plus',
  Max = 'max',
}

export enum ESchoolLevelType {
  None = 'none',
  Preschool = 'preschool',
  Elementary = 'elementary',
  Middle = 'middle',
  High = 'high',
  University = 'university',
}

export enum EGenMode {
  None = '',

  /* Content generators */
  GenEq = 'gen-eq',
  GenSingle = 'gen-single',
  GenMulti = 'gen-multi',
  GenList = 'gen-list',
  GenChain = 'gen-chain',
  GenPart = 'gen-part',

  /* Page layouts */
  LayoutBase = 'layout-base',
  LayoutBaseTest = 'layout-base-test',
  LayoutChain = 'layout-chain',
  LayoutImage = 'layout-image',
  LayoutImageSingle = 'layout-image-single',
  LayoutLabel = 'layout-label',
  LayoutLabelText = 'layout-label-text',
  LayoutList = 'layout-list',
  LayoutMulti = 'layout-multi',
  LayoutSingle = 'layout-single',

  /* Others */
  Gallery = 'gallery',
  Exercise = 'exercise',
  Static = 'static',
  AnyQr = 'any-qr',
}

export interface IAvailability {
  free: boolean;
  licenceLevels: ELicenceLevelType[];
  schoolLevels: ESchoolLevelType[];
  events: string[];
}

export interface ICatalogItem extends IAvailability {
  tags: string[];
}

export interface IDataItem {
  important: boolean;
  sort: number;
  item: string;
  langKey: string;
  image: string;
}

export interface ILangItem {
  langKey: string;
  item: string;
}

export interface InfoInfo {
  pages: number;
}

export interface DownloadInfo extends ICatalogItem {
  fileName: string;
  fileSize: number;
  link: string;
  langKey: string;
  pages: number;
  thumbnail?: string;
}

export interface GenDimensionItem {
  key: string;
  langKey: string;
  sort?: number;
}

export interface GenDimension {
  langKey: string;
  items: GenDimensionItem[];
}

export interface GenDataItem extends IDataItem {
  keys: string[];
  type?: 'archive';
  archive?: string;
  file?: string;
}

export interface GenMatrix {
  dimensions: GenDimension[];
  data: GenDataItem[];
}

export interface GenInfo {
  matrix: GenMatrix;
}

export interface SeoInfo {
  title?: string;
  description?: string;
  keywords?: string[];
}

export interface ArticleMetadata extends IDataItem, ICatalogItem {
  generated: boolean;
  langs: string[];
  download: DownloadInfo[];
  gen: GenInfo;
  info: InfoInfo;
  genModeType: EGenMode;
  manual?: Record<string, string>;
  rvp?: RvpItem[];
  description?: Record<string, string>;
  seo?: Record<string, SeoInfo>;
}

export interface ArticleInfo {
  metadata: ArticleMetadata;
  translations: Record<string, string>;
}

export interface ProcessorInfo extends IDataItem, ICatalogItem {
  mode: EGenMode;
  staticFile?: string;
  type?: 'archive';
  archive?: string;
  file?: string;
}

export interface EventInfo {
  id: string;
  day: number;
  month: number;
  langKey: string;
  isExactDay: boolean;
  daysBefore: number;
  daysAfter: number;
  priority: number;
}

export interface GalleryTranslation {
  key: string;
  text: string;
  files: Record<string, string>;
}

export interface GalleryCategory {
  id: string;
  lang: Record<string, string>;
  items: string[];
}

export interface GalleryCategoryType {
  id: string;
  lang: Record<string, string>;
  categories: GalleryCategory[];
}

export interface ServerGalleryInfo extends ICatalogItem {
  item: string;
  langKey: string;
  image: string;
  important: boolean;
  sort: number;
  files: string[];
  translations: GalleryTranslation[];
  categories?: GalleryCategoryType[];
}

export interface ClientGalleryImage {
  id: string;
  data: string;
  type: 'jpeg' | 'png';
  width: number;
  height: number;
  label?: string;
  tupleLabel?: string;
}

export interface RvpItem {
  code: string;
  title: string;
  reason?: string;
  usage?: string;
}

export interface ClientGallery {
  id: string;
  name: string;
  images: ClientGalleryImage[];
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  articles: ArticleInfo[];
  processors: ProcessorInfo[];
  serverGalleries: ServerGalleryInfo[];
  events: EventInfo[];
}
